<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNavigationContent } from '@/composables/useContent'
import { useTheme } from '@/composables/useTheme'

// Props
const props = defineProps({
  navigationData: {
    type: Object,
    default: null
  }
})

// Setup
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { isDark, toggleTheme } = useTheme()
const mobileMenuOpen = ref(false)
const { navigation, site, isLoading, error } = useNavigationContent()

const isLegalPage = computed(() => ['TermsAndConditions', 'PrivacyPolicy'].includes(route.name))

// Usar props si se pasan, sino usar contenido por defecto
const navContent = computed(() => props.navigationData || navigation.value)
const siteContent = computed(() => site.value)

// Methods
const handleLogout = async () => {
  try {
    await authStore.logout()
    router.push('/')
    mobileMenuOpen.value = false
  } catch (error) {
    console.error('Error en logout:', error)
  }
}

const closeMobileMenu = () => {
  mobileMenuOpen.value = false
}
</script>

<template>
  <header class="sticky top-0 bg-light-background/95 dark:bg-dark-background/95 backdrop-blur-sm shadow-sm dark:shadow-xl dark:border-b dark:border-dark-border z-10 transition-colors duration-300">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
      <!-- Logo -->
      <router-link 
        to="/" 
        class="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary" 
        @click="closeMobileMenu"
      >
        GIOR<span class="text-light-accent dark:text-dark-accent">.mx</span>
      </router-link>
      
      <!-- Desktop Navigation -->
      <nav v-if="!isLegalPage" class="hidden md:flex space-x-8">
        <a 
          v-for="item in navContent.main"
          :key="item.id"
          :href="`#${item.path.replace('/', '')}`"
          v-show="!item.requireAuth || authStore.isAuthenticated"
          class="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition duration-150"
        >
          {{ item.label }}
        </a>
        <a href="#features" class="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition duration-150">
          Características
        </a>
        <a href="#pricing" class="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition duration-150">
          Precios
        </a>
        <a href="#contact" class="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary transition duration-150">
          Contacto
        </a>
      </nav>

      <!-- Actions -->
      <div class="flex items-center space-x-4">
        <!-- Authenticated User -->
        <div v-if="authStore.isAuthenticated" class="hidden lg:flex items-center space-x-3">
          <div class="flex items-center space-x-2">
            <div 
              :class="[
                'w-8 h-8 rounded-full flex items-center justify-center overflow-hidden',
                authStore.avatarUrl ? 'bg-transparent' : 'bg-light-accent/20 dark:bg-dark-accent/20'
              ]"
            >
              <!-- Misma URL resuelta que en perfil (UUID → presignada o URL directa) -->
              <img 
                v-if="authStore.avatarUrl"
                :src="authStore.avatarUrl"
                :alt="authStore.userName"
                class="w-full h-full object-cover"
              />
              <span 
                v-else
                class="text-light-accent dark:text-dark-accent font-medium text-sm"
              >
                {{ authStore.userName.charAt(0).toUpperCase() }}
              </span>
            </div>
            <span class="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
              {{ authStore.userName }}
            </span>
          </div>
          
          <button 
            @click="handleLogout"
            class="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary hidden lg:inline transition duration-150"
          >
            Cerrar Sesión
          </button>
          
          <router-link 
            to="/dashboard"
            class="header-static-cta px-5 py-2 rounded-lg font-semibold transition duration-300 shadow-md"
          >
            Dashboard
          </router-link>
        </div>
        
        <!-- Guest User -->
        <div v-else class="hidden lg:flex items-center space-x-4">
          <router-link 
            to="/login"
            class="header-static-login hidden sm:inline transition duration-150"
          >
            Iniciar Sesión
          </router-link>
          
          <router-link 
            to="/register"
            class="header-static-cta px-5 py-2 rounded-lg font-semibold transition duration-300 shadow-md"
          >
            Regístrate Gratis
          </router-link>
        </div>

        <!-- Theme Toggle -->
        <div class="flex items-center">
          <button
            @click="toggleTheme"
            :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
            class="header-theme-toggle relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
            :class="[
              isDark 
                ? 'is-dark' 
                : 'is-light'
            ]"
          >
            <span
              class="header-theme-thumb h-4 w-4 transform rounded-full transition-transform duration-300 shadow-lg ring-0 flex items-center justify-center"
              :class="[
                isDark ? 'translate-x-6' : 'translate-x-1'
              ]"
            >
              <!-- Icon inside the toggle -->
              <svg 
                v-if="!isDark"
                :class="[
                  'w-3 h-3 transition-all duration-300',
                  'text-light-text-secondary dark:text-dark-text-secondary'
                ]"
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <!-- Moon icon -->
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
              <svg 
                v-else
                :class="[
                  'w-3 h-3 transition-all duration-300',
                  'accent-text'
                ]"
                fill="currentColor" 
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <!-- Sun icon - more distinctive -->
                <circle cx="12" cy="12" r="5" fill="currentColor"/>
                <line x1="12" y1="1" x2="12" y2="3" stroke="currentColor" stroke-width="2"/>
                <line x1="12" y1="21" x2="12" y2="23" stroke="currentColor" stroke-width="2"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="currentColor" stroke-width="2"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="currentColor" stroke-width="2"/>
                <line x1="1" y1="12" x2="3" y2="12" stroke="currentColor" stroke-width="2"/>
                <line x1="21" y1="12" x2="23" y2="12" stroke="currentColor" stroke-width="2"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="currentColor" stroke-width="2"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="currentColor" stroke-width="2"/>
              </svg>
            </span>
          </button>
        </div>

        <!-- Mobile menu button -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="lg:hidden p-2 rounded-md text-light-text-primary dark:text-dark-text-primary hover:bg-light-border dark:hover:bg-dark-border transition duration-300"
        >
          <span v-if="!mobileMenuOpen" class="text-xl">☰</span>
          <span v-else class="text-xl">✕</span>
        </button>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div 
      v-if="mobileMenuOpen" 
      class="lg:hidden bg-light-background dark:bg-dark-background border-t border-light-border dark:border-dark-border transition-colors duration-300"
    >
      <div class="px-4 py-3 space-y-3">
        <!-- Navigation Links (ocultos en páginas legales) -->
        <template v-if="!isLegalPage">
          <a 
            v-for="item in navContent.main"
            :key="`mobile-${item.id}`"
            :href="`#${item.path.replace('/', '')}`"
            v-show="!item.requireAuth || authStore.isAuthenticated"
            @click="closeMobileMenu"
            class="block text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary py-2 transition duration-150"
          >
            {{ item.label }}
          </a>
          
          <a href="#features" @click="closeMobileMenu" class="block text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary py-2 transition duration-150">
            Características
          </a>
          <a href="#pricing" @click="closeMobileMenu" class="block text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary py-2 transition duration-150">
            Precios
          </a>
          <a href="#contact" @click="closeMobileMenu" class="block text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary py-2 transition duration-150">
            Contacto
          </a>
        </template>
        
        <!-- Authentication for mobile -->
        <div v-if="authStore.isAuthenticated" class="border-t border-light-border dark:border-dark-border pt-3 mt-3">
          <div class="flex items-center py-2">
            <div 
              :class="[
                'w-8 h-8 rounded-full flex items-center justify-center overflow-hidden',
                authStore.avatarUrl ? 'bg-transparent' : 'bg-light-accent/20 dark:bg-dark-accent/20'
              ]"
            >
              <img 
                v-if="authStore.avatarUrl"
                :src="authStore.avatarUrl"
                :alt="authStore.userName"
                class="w-full h-full object-cover"
              />
              <span 
                v-else
                class="text-light-accent dark:text-dark-accent font-medium text-sm"
              >
                {{ authStore.userName.charAt(0).toUpperCase() }}
              </span>
            </div>
            <span class="ml-3 text-light-text-primary dark:text-dark-text-primary font-medium">
              {{ authStore.userName }}
            </span>
          </div>
          
          <router-link 
            to="/dashboard"
            @click="closeMobileMenu"
            class="header-static-cta block w-full px-4 py-2 rounded-lg font-semibold text-center mt-3 transition duration-300"
          >
            Dashboard
          </router-link>
          
          <button 
            @click="handleLogout"
            class="block w-full text-left text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary py-2 mt-2 transition duration-150"
          >
            Cerrar Sesión
          </button>
        </div>
        
        <!-- Guest menu for mobile -->
        <div v-else class="border-t border-light-border dark:border-dark-border pt-3 mt-3 space-y-3">
          <router-link 
            to="/login"
            @click="closeMobileMenu"
            class="header-static-login block py-2 transition duration-150"
          >
            Iniciar Sesión
          </router-link>
          <router-link 
            to="/register"
            @click="closeMobileMenu"
            class="header-static-cta block px-4 py-2 rounded-lg font-semibold text-center transition duration-300"
          >
            Regístrate Gratis
          </router-link>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header-static-cta {
  background: #8c5a3d;
  color: #fdfcfa;
}

.header-static-cta:hover {
  background: #704832;
}

.header-static-login {
  color: #5f5f5f;
}

.header-static-login:hover {
  color: #1f1d20;
}

:global(.dark) .header-static-login {
  color: #848484;
}

:global(.dark) .header-static-login:hover {
  color: #fdfcfa;
}

.header-theme-toggle {
  background: #e0e0e0;
}

.header-theme-toggle:focus {
  box-shadow: 0 0 0 2px rgba(53, 57, 64, 0.2);
}

.header-theme-toggle.is-dark {
  background: #353940;
}

.header-theme-thumb {
  background: #fdfcfa;
}

:global(.dark) .header-theme-thumb {
  background: #1f1d20;
}
</style>
